import { Breadcrumb, ConfigProvider, DatePicker, PaginationProps, Select, TreeSelect } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Layout from '../../../Layout';
import { CampaignStats, getReportVoucherByCampaign, getReportVoucherCustomerDistributionByAge, getReportVoucherCustomerDistributionByJob, getReportVoucherCustomerDistributionByRegion, getReportVoucherDemographics, IChartCustomerReportRank, IChartMemberFluctuation, IChartReportRankDemographics, IReportCampaignVehicleDistributionByAgeV2, IReportVoucherByCampaign } from '../../../api/reportCustomer/reportCustomerApi';
import { getCLTypeVoucher } from '../../../api/selectOption/selectOptionsApi';
import { getListCampaign, getSLUnitByPermission, ISelectOption } from '../../../api/selectOptions/selectOptionsApi';
import { convertNationwideValueToTree_V2 } from '../../../api/unit/unitApi';
import useDebounce from '../../../hook/useDebounce';
import useLoading from '../../../hook/useLoading';
import useThrottle from '../../../hook/useThrottle';
import useToast from '../../../hook/useToast';
import { formatNumber } from '../../../services/mixin';
import { selectorInfoUser } from '../../../store/slices/infoUserSlice';
import { selectorPermission } from '../../../store/slices/permissionSlice';
import { fommatSelectOptionTitle, formatPrice, getUserPermissions } from '../../../until';
import { ChartCard } from './components/ChartCard';
import { CustomerTypePie } from './components/CustomerTypePie';
import { CustomerTypePieBusiness } from './components/CustomerTypePieBusiness';
import { GenderDonut } from './components/GenderDonut';
import { HorizontalBarChart } from './components/HorizontalBarChart';
import ReportTableTotalMembers from './components/ReportTableTotalMembers';
import VoucherCampaignReportTable from './components/VoucherCampaignReportTable';
import VoucherRedeemedByUnitMonthlyTable from './components/VoucherRedeemedByUnitMonthlyTable';
import './styles.scss';

const { RangePicker } = DatePicker;

export enum TYPEFILTERTOP {
    ADDRESS = 0,
    JOB = 1,
}


interface CustomerGrowthData {
    year: number;
    totalCustomers: number;
    growthCount: number;
    growthRate: number;
}

interface CustomerGrowthChartData {
    years: string[];
    totalCustomers: number[];
    growthRates: number[];
}

export interface ChartData {
    name: string;
    value: number;
    color: string;
    id?: number;
}

const DEFAULT_COLORS = [
    "#3D99F5", // Member
    "#39C2C9", // Silver
    "#45C35B", // Gold
    "#FFC928", // Plantium
    "#F15672", // Diamond
    "#8B56DD", // Diamond 1
    "#4E56D9", // Diamond 2
    "#344A91", // Không xác định
];

const randomColor = (): string => {
    const letters = "0123456789ABCDEF";

    let color = "#";

    for (let i = 0; i < 6; i++) {
        color += letters[
            Math.floor(Math.random() * 16)
        ];
    }

    return color;
};

const convertRankDataToChartData = (
    data: IChartCustomerReportRank[] | undefined | null
): ChartData[] => {

    if (!data || data.length === 0) {
        return [];
    }

    return data.map((item, index) => ({
        name: item.rankName,
        value: item.count,
        color:
            index < DEFAULT_COLORS.length
                ? DEFAULT_COLORS[index]
                : randomColor(),
        id: item.rankId,
    }));
};




const convertCustomerGrowthData = (
    data: IChartMemberFluctuation[] | undefined | null
): CustomerGrowthChartData => {

    if (!data?.length) {
        return {
            years: [],
            totalCustomers: [],
            growthRates: [],
        };
    }

    return {
        years: data.map(item => String(item.year)),

        totalCustomers: data.map(
            item => item.totalCustomers ?? 0
        ),

        growthRates: data.map(
            item => item.growthRate ?? 0
        ),
    };
};


const buildTopWithOther = (
    data: { name: string; value: number }[],
    top: number
) => {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    const main = sorted.slice(0, top - 1);
    const otherSum = sorted.slice(top - 1).reduce((s, i) => s + i.value, 0);

    return otherSum > 0 ? [...main, { name: "Khác", value: otherSum }] : main;
};


const buildTopWithOtherV2 = (
    data: { id: number; name: string; value: number }[] | undefined,
    top: number
): { id: number[]; name: string; value: number }[] => {

    if (!data?.length) return [];

    const sorted = [...data].sort(
        (a, b) => b.value - a.value
    );

    // top = 15 => lấy toàn bộ, không gom "Khác"
    if (top === 15) {
        return sorted.map(item => ({
            ...item,
            id: [item.id],
        }));
    }

    // Các item chính
    const main = sorted
        .slice(0, top - 1)
        .map(item => ({
            ...item,
            id: [item.id],
        }));

    // Các item còn lại gom vào "Khác"
    const others = sorted.slice(top - 1);

    const otherSum = others.reduce(
        (sum, item) => sum + item.value,
        0
    );

    const otherIds = others.map(
        item => item.id
    );

    return otherSum > 0
        ? [
            ...main,
            {
                name: "Khác",
                value: otherSum,
                id: otherIds,
            },
        ]
        : main;
};


const convertAgeRangeToValue = (value?: string | null): number | undefined => {
    if (!value) return undefined;

    const normalized = value
        .trim()
        .replace(/\s+/g, "");

    const ageRangeMap: Record<string, number> = {
        "Khôngxácđịnh": 0,
        "<20": 1,
        "20-25": 2,
        "26-29": 3,
        "30-39": 4,
        "40-49": 5,
        "50-59": 6,
        "60+": 7
    };

    return ageRangeMap[normalized];
};


export interface TypeTotalReport {
    zns?: CampaignStats
    sms?: CampaignStats
    email?: CampaignStats
}

export interface IDataDefaulFilter {
    unitIds?: number[],
    campaignIds?: number[],
    dateStart?: string,
    dateEnd?: string,
    type?: number,
    typeVoucherId?: number,
}

const VoucherOverview: React.FC = () => {
    const pushToast = useToast();
    const pushLoading = useLoading()
    const navigate = useNavigate()

    const location = useLocation();
    const { unitTemp } = useSelector(selectorInfoUser)

    const permissionSlice = useSelector(selectorPermission)
    const { add, edit, delete: isdelete, upload, download } = getUserPermissions(permissionSlice, location.pathname)
    const [dataReportRankDemographics, setDataReportRankDemographics] = useState<IChartReportRankDemographics>()
    const [dataReportVoucherDistributionByAge, setDataReportVoucherDistributionByAge] = useState<IReportCampaignVehicleDistributionByAgeV2>()
    const [dataReportVoucherCustomerAddress, setDataReportVoucherCustomerAddress] = useState<any[]>()
    const [dataReportVoucherCustomerJob, setDataReportVoucherCustomerJob] = useState<any[]>()

    // data báo cáo hạng thành viên

    const [sLUnitByPermission, setSLUnitByPermission] = useState<any>()
    const [sLTypeVoucher, setSLTypeVoucher] = useState<any[]>([])
    const [sLCampaign, setSLCampaign] = useState<ISelectOption[]>([])


    // pagigation
    const [paginationReportVoucherByCampaign, setPaginationReportVoucherByCampaign] = useState({ current: 1, pageSize: 12, total: 0 })

    // data cho report voucher
    const [dataReportVoucherByCampaign, setDataReportVoucherByCampaign] = useState<IReportVoucherByCampaign[]>()

    const [totalReportVoucherByCampaign, setTotalReportVoucherByCampaign] = useState<{
        totalIssued: number,
        totalRedeemed: number,
        rateRedeemed: number,
    }>()

    const [dataFilter, setDataFilter] = useState<IDataDefaulFilter>()
    const [total, setTotal] = useState<number>()



    const [valueTop, setValueTop] = useState<Record<TYPEFILTERTOP, number>>({
        [TYPEFILTERTOP.ADDRESS]: 10,
        [TYPEFILTERTOP.JOB]: 10,
    });


    const dataVoucherDistributionByAge = useMemo(() => {
        if (!dataReportVoucherDistributionByAge) return [];
        const sum = (key: keyof IReportCampaignVehicleDistributionByAgeV2) => dataReportVoucherDistributionByAge[key] || 0;
        return [
            { name: 'Không xác định', value: dataReportVoucherDistributionByAge.undetermined },
            { name: '< 20', value: dataReportVoucherDistributionByAge.ageUnder20 },
            { name: '20 - 25', value: dataReportVoucherDistributionByAge.ageBetween2025 },
            { name: '26 - 29', value: dataReportVoucherDistributionByAge.ageBetween2629 },
            { name: '30 - 39', value: dataReportVoucherDistributionByAge.ageBetween3039 },
            { name: '40 - 49', value: dataReportVoucherDistributionByAge.ageBetween4049 },
            { name: '50 - 59', value: dataReportVoucherDistributionByAge.ageBetween5059 },
            { name: '60+', value: dataReportVoucherDistributionByAge.ageOver60 },
        ];
    }, [dataReportVoucherDistributionByAge]);




    const dataFormatCustomerAddress = useMemo(() => buildTopWithOtherV2(dataReportVoucherCustomerAddress ?? [], valueTop[TYPEFILTERTOP.ADDRESS])
        , [dataReportVoucherCustomerAddress, valueTop[TYPEFILTERTOP.ADDRESS]])


    const dataFormatCustomerJob = useMemo(() => buildTopWithOtherV2(dataReportVoucherCustomerJob ?? [], valueTop[TYPEFILTERTOP.JOB])
        , [dataReportVoucherCustomerJob, valueTop[TYPEFILTERTOP.JOB]])


    const gSLUnitByPermission = async () => {
        const result = await getSLUnitByPermission(unitTemp);
        if (result?.status) {
            setSLUnitByPermission(convertNationwideValueToTree_V2(result.data))
        } else {
        }
    }

    const gSLTypeVoucher = async () => {
        pushLoading(true)
        const result = await getCLTypeVoucher()
        if (result?.status) {
            setSLTypeVoucher(fommatSelectOptionTitle(result.data))
        } else {
            pushToast(result?.message, "warning")
        }
        pushLoading(false)
    }


    const gSLCampaign = useDebounce(async () => {
        pushLoading(true)
        const result = await getListCampaign();
        if (result?.status) {
            setSLCampaign(fommatSelectOptionTitle(result.data))
        } else {
            pushToast(result?.message, "error")
        }
        pushLoading(false)
    }, 500)

    //#region  call api cho voucher report



    const gListReportVoucherByCampaign = useDebounce(async (pageCurent: number = 1, pageSize: number = 10) => {
        pushLoading(true)
        const result = await getReportVoucherByCampaign(
            pageSize, pageCurent,
            unitTemp, dataFilter?.unitIds, dataFilter?.campaignIds,
            // (YYYY-MM-DD)
            dataFilter?.dateStart, dataFilter?.dateEnd,
            // Loại hình voucher (0 - Công khai, 1 - Theo nhóm, 2 - Theo cá nhân)
            dataFilter?.type,
            dataFilter?.typeVoucherId
        )
        if (result.status) {
            setDataReportVoucherByCampaign(result.data)
            setTotalReportVoucherByCampaign(result.summary)
            setPaginationReportVoucherByCampaign({ current: pageCurent, total: result.total, pageSize })
        }
        pushLoading(false)
    }, 500)



    const gReportVoucherDemographics = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportVoucherDemographics(
            unitTemp, dataFilter?.unitIds, dataFilter?.campaignIds,
            // (YYYY-MM-DD)
            dataFilter?.dateStart, dataFilter?.dateEnd,
            // Loại hình voucher (0 - Công khai, 1 - Theo nhóm, 2 - Theo cá nhân)
            dataFilter?.type,
            dataFilter?.typeVoucherId

        )
        if (result.status) {
            setDataReportRankDemographics(result.data)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)


    // 


    // 


    // getReportVoucherCustomerDistributionByJob



    const gReportVoucherCustomerDistributionByAge = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportVoucherCustomerDistributionByAge(
            unitTemp, dataFilter?.unitIds, dataFilter?.campaignIds,
            // (YYYY-MM-DD)
            dataFilter?.dateStart, dataFilter?.dateEnd,
            // Loại hình voucher (0 - Công khai, 1 - Theo nhóm, 2 - Theo cá nhân)
            dataFilter?.type,
            dataFilter?.typeVoucherId

        )
        if (result.status) {
            setDataReportVoucherDistributionByAge(result.data)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)

    const gReportVoucherCustomerDistributionByRegion = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportVoucherCustomerDistributionByRegion(
            unitTemp, dataFilter?.unitIds, dataFilter?.campaignIds,
            // (YYYY-MM-DD)
            dataFilter?.dateStart, dataFilter?.dateEnd,
            // Loại hình voucher (0 - Công khai, 1 - Theo nhóm, 2 - Theo cá nhân)
            dataFilter?.type,
            dataFilter?.typeVoucherId

        )
        if (result.status) {
            const raw = result.data.map(item => ({
                name: item.provinceName,
                value: item.total,
                id: item.provinceId
            }));
            setDataReportVoucherCustomerAddress(raw)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)



    const gReportVoucherCustomerDistributionByJob = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportVoucherCustomerDistributionByJob(
            unitTemp, dataFilter?.unitIds, dataFilter?.campaignIds,
            // (YYYY-MM-DD)
            dataFilter?.dateStart, dataFilter?.dateEnd,
            // Loại hình voucher (0 - Công khai, 1 - Theo nhóm, 2 - Theo cá nhân)
            dataFilter?.type,
            dataFilter?.typeVoucherId
        )
        if (result.status) {
            const raw = result.data.map(item => ({
                name: item.jobName,
                id: item.jobId,
                value: item.total,
            }));
            setDataReportVoucherCustomerJob(raw)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)



    //#endregion

    const onDateChange = useThrottle((date: any) => {
        if (date?.[0] && date?.[1]) {
            setDataFilter({
                ...dataFilter,
                dateStart: date[0].format('YYYY-MM-DD'),
                dateEnd: date[1].format('YYYY-MM-DD'),
            });
        } else {
            setDataFilter({
                ...dataFilter,
                dateStart: '',
                dateEnd: '',
            });
        }
    }, 2000)





    const onPaginationChange: PaginationProps['onChange'] = (pageNumber, pageSize) => {
        gListReportVoucherByCampaign(pageNumber, pageSize)
    }


    useEffect(() => {

        gReportVoucherDemographics()
        gListReportVoucherByCampaign()
        gReportVoucherCustomerDistributionByAge()
        gReportVoucherCustomerDistributionByRegion()
        gReportVoucherCustomerDistributionByJob()

    }, [dataFilter, unitTemp])



    useEffect(() => {
        // gSLBrandName()
        gSLUnitByPermission()

    }, [unitTemp])

    useEffect(() => {
        // gSLBrandName()
        gSLTypeVoucher()
        gSLCampaign()
    }, [])




    return (
        <div className=''>
            <Layout  className='custom-sidebar-chart'>

                <Breadcrumb
                    items={[
                        {
                            title: 'THACO AUTO CRM',
                        },
                        {
                            title: <a href="/">Báo cáo phân tích</a>,
                        },
                        {
                            title: 'Tổng quan voucher',
                        },
                    ]}
                />

                <div className='title-page'>
                    Tổng quan voucher
                </div>


                <div className="custom-page-css w-full flex gap-2">
                    <div className=" custom-page-css   ">
                        <TreeSelect
                            className='custom-page-css border rounded overflow-hidden w-300px custom-scroll-multi-select'
                            multiple
                            showSearch
                            placeholder="Đơn vị"
                            allowClear
                            value={dataFilter?.unitIds}
                            treeDefaultExpandAll={false}
                            treeData={sLUnitByPermission}
                            filterTreeNode={(input: string, option: any) =>
                                typeof option?.label === 'string' && option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            onChange={(e) => setDataFilter({ ...dataFilter, unitIds: e !== undefined ? e : undefined })}
                        />
                    </div>
                    <div className=" custom-page-css   ">

                        <Select
                            className='custom-scroll-multi-select custom-page-css border rounded overflow-hidden  w-200px  '
                            placeholder="Chiến dịch"
                            allowClear
                            showSearch={false}
                            mode="multiple"
                            onClear={() => setDataFilter({ ...dataFilter, campaignIds: undefined })}
                            onChange={(e) => setDataFilter({ ...dataFilter, campaignIds: e !== undefined ? e : undefined })}
                            value={dataFilter?.campaignIds}
                            options={sLCampaign}
                        />
                    </div>
                    <div className="  className='custom-page-css overflow-hidden  ">
                        <ConfigProvider locale={viVN}>
                            <RangePicker
                                format={'DD/MM/YYYY'}
                                className="custom-input-date"
                                popupClassName="rs-range-picker-dropdown"
                                placeholder={['Chọn từ ngày', 'Đến ngày']}
                                value={(dataFilter?.dateStart && dataFilter?.dateEnd) ? [dayjs(dataFilter?.dateStart), dayjs(dataFilter?.dateEnd)] : null}
                                onChange={onDateChange}
                            />
                        </ConfigProvider>
                    </div>
                    <Select
                        className='custom-page-css border rounded overflow-hidden  w-200px  '
                        placeholder="Loại hình voucher"
                        allowClear
                        showSearch={false}
                        onClear={() => setDataFilter({ ...dataFilter, type: undefined })}
                        onChange={(e) => setDataFilter({ ...dataFilter, type: e !== undefined ? e : undefined })}
                        value={dataFilter?.type}
                        options={[
                            { value: 0, label: 'Voucher công khai' },
                            { value: 1, label: 'Voucher cho nhóm' },
                            { value: 2, label: 'Voucher cá nhân' },
                        ]}
                    />
                    <Select
                        className='custom-page-css border rounded overflow-hidden  w-200px  '
                        placeholder="Loại voucher"
                        allowClear
                        showSearch={false}

                        options={sLTypeVoucher}
                        onClear={() => setDataFilter({ ...dataFilter, typeVoucherId: undefined })}
                        onChange={(e) => setDataFilter({ ...dataFilter, typeVoucherId: e !== undefined ? e : undefined })}
                        value={dataFilter?.typeVoucherId}
                    />
                </div>

                <VoucherCampaignReportTable
                    data={dataReportVoucherByCampaign}
                    totalReportVoucherByCampaign={totalReportVoucherByCampaign}
                    pagination={paginationReportVoucherByCampaign}
                    onPaginationChange={onPaginationChange}
                />

                <VoucherRedeemedByUnitMonthlyTable
                    dataFilter={dataFilter}

                />


                <span className='text-base font-medium pt-4 pb-2'>Báo cáo voucher được quy đổi tại công ty tỉnh thành</span>
                <ReportTableTotalMembers dataFilter={dataFilter} setTotal={setTotal} />

                <div className='custom-page-css flex flex-col gap-1 pt-4'>
                    <span className='custom-page-css text-base font-medium'> Báo cáo nhân khẩu học khách hàng</span>
                    <span className='custom-page-css text-base font-regular text-italic text-344054'>
                        Đang hiển thị dữ liệu tổng hợp từ{" "}
                        <span
                            style={{
                                color: "#1677FF",
                                fontWeight: 500,
                                fontStyle: "normal",
                                padding: '0 4px'
                            }}
                        >
                            {formatNumber(total ?? 0)} đơn vị
                        </span>{" "}
                        phía trên
                    </span>

                </div>

                <div className="custom-page-css flex items-stretch gap-4 pt-4">
                    <div
                        className="custom-page-css flex flex-1 "
                    >
                        <ChartCard title="Biểu đồ tỉ lệ phân loại khách hàng" >
                            <CustomerTypePieBusiness
                                data={dataReportRankDemographics?.businessCheck}
                                dataFilter={dataFilter}
                            />
                        </ChartCard>
                    </div>
                    <div
                        className="custom-page-css flex flex-1 "
                    >
                        <ChartCard title="Biểu đồ tỉ lệ giới tính" >
                            <GenderDonut
                                data={dataReportRankDemographics?.gender}
                                dataFilter={dataFilter}

                            />
                        </ChartCard>
                    </div>

                    <div
                        className="custom-page-css flex flex-1 ">
                        <ChartCard title="Biểu đồ  tỉ lệ tình trạng hôn nhân" >
                            <CustomerTypePie
                                data={dataReportRankDemographics?.statusMarital}
                                dataFilter={dataFilter}

                            />
                        </ChartCard>
                    </div>
                </div>

                <div className="custom-page-css flex items-stretch gap-4 pt-4">
                    <div
                        className="custom-page-css flex flex-col flex-1 border rounded-2 gap-2 p-6"
                        style={{ border: '1px solid #eaebec' }}
                    >
                        <div className="custom-page-css flex justify-between items-center mb-2">
                            <h3 className="custom-page-css text-344054 text-base font-medium">Biểu đồ phân bố khách hàng theo độ tuổi</h3>
                        </div>
                        <div style={{ width: '100%', height: 420 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={dataVoucherDistributionByAge}
                                    margin={{ top: 8, right: 20, left: 20, bottom: 8 }}

                                    onClick={(params) =>
                                        navigate(
                                            `/analysis-report/customers-card-list`,
                                            {
                                                state: {
                                                    firstPage: '/analysis-report/voucher-overview',
                                                    reportType: 4,
                                                    dataFilterVoucher: dataFilter,
                                                    ageGroupVoucher: convertAgeRangeToValue(params.activeLabel),
                                                    title: `Khách hàng ở độ tuổi ${params.activeLabel}`

                                                }
                                            }

                                        )

                                        // console.log('dataCampaignVehicleDistributionByAge', convertAgeRangeToValue(params.activeLabel))

                                    }
                                >
                                    <defs>
                                        <linearGradient id="ownershipGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#1890FF" stopOpacity={0.9} />
                                            <stop offset="95%" stopColor="#9DD0FF" stopOpacity={0.7} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                    <YAxis tickFormatter={(value: number) => formatPrice(value)} />
                                    {dataVoucherDistributionByAge.length > 0 && (
                                        <Tooltip
                                            labelFormatter={(label) => ` ${label}`}
                                            formatter={(value: number) => [`${formatNumber(value ?? 0)}`, 'Giá trị']}
                                        />
                                    )}
                                    <Bar dataKey="value" fill="url(#ownershipGradient)" radius={[4, 4, 0, 0]} barSize={60} />

                                </BarChart>
                            </ResponsiveContainer>

                        </div>
                    </div>
                </div>


                <div className="custom-page-css flex items-stretch gap-4 pt-4">
                    <div
                        className="custom-page-css flex flex-1">
                        <HorizontalBarChart
                            type={TYPEFILTERTOP.ADDRESS}
                            title="Top khách hàng theo khu vực"
                            data={dataFormatCustomerAddress}
                            valueTop={valueTop}
                            setValueTop={setValueTop}
                            color="#8979FF"
                            dataFilter={dataFilter}

                        />
                    </div>

                    <div
                        className="custom-page-css flex flex-1">
                        <HorizontalBarChart
                            type={TYPEFILTERTOP.JOB}
                            title="Top khách hàng theo nghề nghiệp"
                            data={dataFormatCustomerJob}
                            valueTop={valueTop}
                            setValueTop={setValueTop}
                            color="#1890FF"
                            dataFilter={dataFilter}

                        />
                    </div>
                </div>

            </Layout>
        </div>
    );
}

export default VoucherOverview;
